import React, { useEffect, useRef, useContext } from "react";
import {
  Box,
  Theme,
  Grid,
  MenuItem,
  ListSubheader,
  FormControl,
  InputLabel,
  TextField,
  Select,
  InputAdornment,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { groupBy } from "lodash";
import { Formik, FormikHelpers, FormikProps } from "formik";

import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

// Model Imports
import { FormType } from "models/FormType";
import { Country, County, GeographyType, State } from "models/Geography";
import { Language } from "models/Langauges";

// API Imports
import { QueryPayload } from "../../../../apis/FormsAPI";
import { useAuthContext } from "AuthContextWrapper"

export interface FormQueryValues {
  country?: Country;
  state?: State;
  county?: County;
  formType?: FormType;
  language?: Language;
  searchTerms?: string;
  geographyInclusive?: boolean;
}

export interface FormsLibrarySearchProps {
  onSelectCountry?: (c?: Country) => void;
  onSelectState?: (s?: State) => void;
  onSelectCounty?: (ct?: County) => void;
  onSelectFormType?: (ft: FormType) => void;
  onSelectLanguage?: (lang: Language) => void;
  initGeographyType?: GeographyType;
  initGeographyId?: number;
  initFormTypeSlug?: string;
  initLanguageCode?: string;
  initSearchTerms?: string;
  geographyInclusive?: boolean;
  onComplete: (values: QueryPayload) => void;
  loadingForms: boolean;
  disableGeographySelect?: boolean;
  presubmit?: boolean;
  hideHeader?: boolean;
}

const FormsLibrarySearch = ({
  initGeographyType,
  initGeographyId,
  initFormTypeSlug = "probate", // Default to Probate forms for now
  initLanguageCode,
  initSearchTerms,
  geographyInclusive = false,
  onComplete,
  loadingForms,
  disableGeographySelect = false,
  hideHeader = false,
}: FormsLibrarySearchProps): JSX.Element => {
  const formRef = useRef<FormikProps<FormQueryValues>>(null);

  const { countries, states, formTypes, languages } = useAuthContext()

  const [initState, setInitState] = React.useState<State | undefined>(
    undefined
  );
  const [initCountry, setInitCountry] = React.useState<Country | undefined>(
    undefined
  );

  useEffect(() => {
    if (states && states.length && countries && countries.length) {
      let foundCountry: Country | undefined = undefined;
      let foundState: State | undefined = undefined;
      switch (initGeographyType) {
        case GeographyType.Country:
          foundCountry = countries?.find(
            (country) => country.id == initGeographyId
          );
          setInitCountry(foundCountry);
          break;
        case GeographyType.State:
          foundState = states?.find((state) => state.id == initGeographyId);
          foundCountry = countries?.find(
            (country) => country.id == foundState?.countryId
          );
          setInitState(foundState);
          setInitCountry(foundCountry);
          break;
        case GeographyType.County:
          console.error("Init County not yet supported!");
          break;
      }
    }
  }, [states, countries]);

  const formikInit = {
    values: {
      country: initCountry,
      state: initState,
      formType: formTypes?.find((type) => type.slug == initFormTypeSlug),
      language: languages?.find((lang) => lang.code == initLanguageCode),
      searchTerms: initSearchTerms,
      geographyInclusive: geographyInclusive,
    } as FormQueryValues,
  };

  const createPayloadAndSubmit = async (values: FormQueryValues) => {
    if (!values.country && !values.state) {
      throw new Error("Estate Location is required");
    }
    if (!values.country && values.state) {
      throw new Error("Country is required if state is Selected");
    }
    if (!values.state && values.county) {
      throw new Error("State is required if county is Selected");
    }
    if (
      values.country &&
      values.state &&
      values.country?.id != values.state?.countryId
    ) {
      throw new Error("State must be within selected Country.");
    }
    if (
      values.state &&
      values.county &&
      values.state?.id != values.county?.stateId
    ) {
      throw new Error("County must be within selected State.");
    }

    const query: QueryPayload = {
      form_type_id: values.formType?.id,
      search_terms: values.searchTerms,
      language_id: values.language?.id,
      geography_inclusive: values.geographyInclusive,
    };
    if (values.county) {
      query.geography_type = values.county.geographyType;
      query.geography_id = values.county.id;
    } else if (values.state) {
      query.geography_type = values.state.geographyType;
      query.geography_id = values.state.id;
    } else if (values.country) {
      query.geography_type = values.country.geographyType;
      query.geography_id = values.country.id;
    }

    onComplete(query);
  };

  const handleSubmit = async (
    values: FormQueryValues,
    { setSubmitting, setStatus }: FormikHelpers<FormQueryValues>
  ) => {
    setSubmitting(true);
    setStatus("");
    try {
      await createPayloadAndSubmit(values);
    } catch (err: any) {
      setStatus("Sorry, error: " + ((err as Error)?.message || ""));
    }
    setSubmitting(false);
  };

  function getEstateLocationOptions(countries: Country[], states: State[]) {
    const items = [];

    // Sort by Country
    const groupByCountry = groupBy(states, (state) => {
      return state.countryId;
    });

    for (const [key, states] of Object.entries(groupByCountry)) {
      const country = countries.find((c) => c.id == parseInt(key));
      if (country) {
        items.push(
          <ListSubheader
            key={country.code}
            sx={{
              borderTop: "1px solid #e3e3e3",
              borderBottom: "1px solid #e3e3e3",
              backgroundColor: "#F2F2FF",
              fontWeight: "bold",
            }}
          >
            {country.name}
          </ListSubheader>
        );

        for (const state of states) {
          items.push(
            <MenuItem key={state.id} value={state.id}>
              {state.name}
            </MenuItem>
          );
        }
      }
    }
    return items;
  }

  return (
    <Formik
      initialValues={formikInit.values}
      onSubmit={handleSubmit}
      innerRef={formRef}
      enableReinitialize
    >
      {({
        handleChange,
        handleSubmit,
        isSubmitting,
        values,
        setFieldValue,
        status,
      }) => (
        <>
          <Box
            sx={{
              backgroundColor: "white",
              paddingLeft: "24px",
              paddingRight: "24px",
              borderRadius: "12px",
              paddingTop: "1rem",
              paddingBottom: "48px",
              boxShadow:
                "0px 1px 10px 0px rgba(116, 135, 164, 0.10), 0px 4px 5px 0px rgba(116, 135, 164, 0.11), 0px 2px 4px -1px rgba(116, 135, 164, 0.16)",
            }}
          >
            {!hideHeader && (
              <Box
                sx={{
                  mb: 2,
                }}
              >
                <Typography variant="h5">Atticus Forms Library</Typography>
                <Typography variant="body1" style={{ marginTop: "1rem" }}>
                  Looking for other forms? Search our Forms Library to find or
                  use more than 3,500 forms. Start by searching for a form,
                  choosing a location, or both:
                </Typography>
              </Box>
            )}
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={(theme: Theme) => ({
                display: "flex",
                flexDirection: "row",
                width: "100%",
                gap: "1rem",
                [theme.breakpoints.down("sm")]: {
                  flexDirection: "column",
                },
              })}
            >
              <Grid container sx={{ flex: 1 }} spacing={2}>
                <Grid
                  item
                  xs={12}
                  sm={disableGeographySelect ? 9 : 5}
                  lg={disableGeographySelect ? 10 : 7}
                >
                  <TextField
                    name="searchTerms"
                    fullWidth
                    sx={(theme: Theme) => ({ marginRight: theme.spacing(1) })}
                    value={values.searchTerms || ""}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    variant="outlined"
                  />
                </Grid>
                {!disableGeographySelect && (
                  <Grid item xs={12} sm={4} lg={3}>
                    <FormControl
                      fullWidth
                      sx={{
                        width: "100%",
                      }}
                    >
                      <InputLabel>Estate Location</InputLabel>
                      <Select
                        name="state"
                        disabled={!countries || !states}
                        value={values.state?.id || ""}
                        label="Estate Location"
                        onChange={async (event: any): Promise<any> => {
                          const newState = states?.find(
                            (s) => s.id == event.target.value
                          );
                          const newCountry = countries?.find(
                            (c) => c.id == newState?.countryId
                          );
                          setFieldValue("state", newState);
                          setFieldValue("country", newCountry);
                          // if (newState) {
                          //   fetchCounties && fetchCounties(newState?.id);
                          // }
                        }}
                      >
                        {countries &&
                          states &&
                          getEstateLocationOptions(countries, states)}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid
                  item
                  xs={12}
                  sm={3}
                  lg={2}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <LoadingButton
                    loading={isSubmitting || loadingForms}
                    variant="contained"
                    color="primary"
                    type="submit"
                    endIcon={<ArrowForwardIcon />}
                    fullWidth
                  >
                    Search
                  </LoadingButton>
                </Grid>
                {status && (
                  <Grid item xs={12}>
                    <Typography variant="body1" color="error">
                      {status}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Box>
        </>
      )}
    </Formik>
  );
};

export default FormsLibrarySearch;
